import React from 'react';
import {
  Table, TableHead, TableBody, TableRow, TableCell, TablePagination,
  Box, Typography, Skeleton, Button,
} from '@mui/material';
import { Truck, SearchX, CircleAlert } from 'lucide-react';
import { ProveedorListItem } from '../types';
import { ProveedorRowCard } from './ProveedorRowCard';

interface Props {
  proveedores: ProveedorListItem[];
  total: number;
  pagina: number;
  limite: number;
  loading: boolean;
  error?: string | null;
  onEditar: (p: ProveedorListItem) => void;
  onEliminar: (p: ProveedorListItem) => void;
  onCambiarPagina: (pagina: number) => void;
  onCambiarLimite: (limite: number) => void;
  busquedaActiva?: boolean;
  onRecargar?: () => void;
  onCrearProveedor?: () => void;
}

const HEADER_COLS = [
  { label: 'Proveedor', width: '40%' },
  { label: 'Ubicación', width: '25%' },
  { label: 'Contactos', width: '20%' },
  { label: 'Acciones', width: '15%' },
];

function EmptyState({
  icon, title, description, actionLabel, onAction,
}: {
  icon: React.ReactNode; title: string; description: string; actionLabel: string; onAction?: () => void;
}) {
  return (
    <TableRow>
      <TableCell colSpan={4} sx={{ border: 'none', py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color: '#A0A0A8' }}>{icon}</Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#3E353F' }}>{title}</Typography>
          <Typography sx={{ fontSize: 13, color: '#756D77', textAlign: 'center', maxWidth: 320 }}>
            {description}
          </Typography>
          {onAction && (
            <Button variant="outlined" size="small" onClick={onAction}
              sx={{ mt: 0.5, borderColor: '#D9AACA', color: '#9E427F', borderRadius: '8px' }}>
              {actionLabel}
            </Button>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function ProveedoresTable({
  proveedores, total, pagina, limite, loading, error,
  onEditar, onEliminar, onCambiarPagina, onCambiarLimite,
  busquedaActiva, onRecargar, onCrearProveedor,
}: Props) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
        <Table
          data-testid="proveedores-table"
          sx={{ borderCollapse: 'separate', borderSpacing: '0 10px', width: '100%' }}
        >
          <TableHead>
            <TableRow sx={{ position: 'sticky', top: 0, background: '#F6F4F6', zIndex: 1, '& th': { border: 'none' } }}>
              {HEADER_COLS.map((col) => (
                <TableCell
                  key={col.label}
                  sx={{
                    width: col.width, height: 42, fontSize: 18, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.02em',
                    color: '#756D77', px: '18px', py: 0, background: '#F6F4F6',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i} sx={{ '& td': { border: 'none', py: 0.5 } }}>
                  {HEADER_COLS.map((col) => (
                    <TableCell key={col.label}>
                      <Skeleton variant="rectangular" height={76} sx={{ borderRadius: 1 }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <EmptyState
                icon={<CircleAlert size={44} />}
                title="No fue posible cargar los proveedores"
                description="Revisa tu conexión e intenta nuevamente."
                actionLabel="Reintentar"
                onAction={onRecargar}
              />
            ) : proveedores.length === 0 && busquedaActiva ? (
              <EmptyState
                icon={<SearchX size={44} />}
                title="No encontramos proveedores"
                description="Prueba con otra razón social o NIT."
                actionLabel="Limpiar búsqueda"
                onAction={onRecargar}
              />
            ) : proveedores.length === 0 ? (
              <EmptyState
                icon={<Truck size={44} />}
                title="Aún no tienes proveedores"
                description="Crea el primer proveedor para comenzar a gestionar tu catálogo."
                actionLabel="Crear proveedor"
                onAction={onCrearProveedor}
              />
            ) : (
              proveedores.map((p) => (
                <ProveedorRowCard key={p.id} proveedor={p} onEditar={onEditar} onEliminar={onEliminar} />
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {!loading && !error && proveedores.length > 0 && (
        <Box sx={{ background: '#FFFFFF', border: '1px solid #E3DEE4', borderRadius: '8px', boxShadow: '0 3px 9px rgba(45,30,46,0.06)', px: '14px', py: '10px' }}>
          <TablePagination
            component="div"
            count={total}
            page={pagina - 1}
            rowsPerPage={limite}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={(_, p) => onCambiarPagina(p + 1)}
            onRowsPerPageChange={(e) => onCambiarLimite(Number(e.target.value))}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ border: 'none', p: 0 }}
          />
        </Box>
      )}
    </Box>
  );
}
